import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { setAccessToken } from '@/lib/auth'
import { APP_NAME } from '@/lib/brand'
import { acceptInviteSchema, type AcceptInviteFormData } from '@/validators/invite'
import type { AuthTokens } from '@/types/auth'

const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
  FINANCIAL: 'Financeiro',
}

interface InviteInfo {
  email: string
  role: string
  /** Nome legível da função (Fatia 5+). Fallback no enum role se ausente. */
  roleName?: string | null
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    data: invite,
    isLoading,
    isError,
    error,
  } = useQuery<InviteInfo>({
    queryKey: ['invite', token],
    queryFn: async () => {
      const res = await api.get<InviteInfo>(`/invites/${token}`)
      return res.data
    },
    retry: false,
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
  })

  async function onSubmit(data: AcceptInviteFormData) {
    setApiError(null)
    try {
      const res = await api.post<AuthTokens>(`/invites/${token}/accept`, data)
      setAccessToken(res.data.accessToken)
      toast.success('Conta criada com sucesso!')
      navigate('/dashboard', { replace: true })
    } catch (err) {
      if (isAxiosError(err)) {
        const msg = err.response?.data?.error?.message as string | undefined
        setApiError(msg ?? 'Erro ao aceitar convite. Tente novamente.')
      } else {
        setApiError('Erro inesperado. Tente novamente.')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Verificando convite…</p>
      </div>
    )
  }

  if (isError) {
    const status = isAxiosError(error) ? error.response?.status : null
    const message =
      status === 410
        ? 'Este convite expirou. Solicite um novo convite ao administrador.'
        : status === 404
          ? 'Convite não encontrado.'
          : 'Não foi possível verificar o convite.'

    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-3 text-center">
          <h1 className="text-xl font-semibold text-foreground">Convite inválido</h1>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{APP_NAME}</h1>
          <p className="text-sm text-muted-foreground">
            Você foi convidado como <strong>{invite!.roleName ?? ROLE_LABEL[invite!.role] ?? invite!.role}</strong>
          </p>
          <p className="text-xs text-muted-foreground">{invite!.email}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Seu nome</Label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              placeholder="Nome completo"
              aria-invalid={!!errors.name}
              {...register('name')}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Crie uma senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>

          {apiError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
            {isSubmitting ? 'Criando conta…' : 'Criar minha conta'}
          </Button>
        </form>
      </div>
    </div>
  )
}
